import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Spin,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Space,
  Tag,
  Empty,
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  EnvironmentOutlined,
  UserOutlined,
  SaveOutlined,
  EditOutlined,
  MailOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries, useWisecureClient } from '@wisecure/api-client/hooks';
import type { MunicipalityResponse, GeoRegionResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { CopyIdButton } from '@/components/CopyIdButton';
import { ConfirmDeleteButton } from '@/components/ConfirmDeleteButton';
import { CommuneMap } from '@/components/CommuneMap';
import { CommuneCoverageStats } from '@/components/CommuneCoverageStats';
import { WisecureCard } from '@/components/base';
import { primary, neutral } from '@/theme';

const { Text, Title } = Typography;

interface GeoRegionItem {
  id: string;
  code: string;
  name: string;
  type: string;
}

interface CommuneFeature {
  properties: {
    id: string;
    code: string;
    name: string;
    areaKm2: number;
  };
}

export function MunicipalityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidateQueries();
  const client = useWisecureClient();
  const [form] = Form.useForm();

  const [selectedGeoIds, setSelectedGeoIds] = useState<string[]>([]);
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [geojsonLoading, setGeojsonLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: municipality, isLoading } = useWisecureQuery<MunicipalityResponse>(
    queryKeys.municipalityDetail(id!),
    (c) => c.municipalities.municipalityControllerFindOne({ id: id! }),
    { enabled: !!id },
  );

  const [geoSearch, setGeoSearch] = useState('');
  const [selectedGeoItems, setSelectedGeoItems] = useState<GeoRegionItem[]>([]);

  useEffect(() => {
    if (municipality?.geoRegions?.length) {
      const ids = municipality.geoRegions.map((gr: GeoRegionResponse) => gr.id);
      client.geoRegions.geoRegionsControllerFindByIds({ requestBody: { ids } }).then((items) => {
        setSelectedGeoItems(items as GeoRegionItem[]);
      });
    }
  }, [municipality, client]);

  const { data: geoRegionsData, isLoading: geoLoading } = useWisecureQuery<{
    data: GeoRegionItem[];
  }>(
    queryKeys.geoRegions(geoSearch),
    (c) =>
      c.geoRegions.geoRegionsControllerFindAll({
        limit: 50,
        type: 'COMMUNE',
        search: geoSearch || undefined,
      }) as Promise<{ data: GeoRegionItem[] }>,
    {
      enabled: !!geoSearch,
    },
  );

  const fetchGeoJSON = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) {
        setGeojson(null);
        return;
      }
      setGeojsonLoading(true);
      try {
        const result = await client.geoRegions.geoRegionsControllerFindByIdsGeoJson({ requestBody: { ids } });
        setGeojson(result);
      } catch {
        setGeojson(null);
      } finally {
        setGeojsonLoading(false);
      }
    },
    [client],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchGeoJSON(selectedGeoIds), 300);
    return () => clearTimeout(timer);
  }, [selectedGeoIds, fetchGeoJSON]);

  useEffect(() => {
    if (municipality?.geoRegions?.length) {
      const ids = municipality.geoRegions.map((gr: GeoRegionResponse) => gr.id);
      setSelectedGeoIds(ids);
    }
  }, [municipality]);

  const updateMutation = useWisecureMutation(
    (c, values: Record<string, unknown>) =>
      c.municipalities.municipalityControllerUpdate({ id: id!, requestBody: values }),
    {
      onSuccess: () => {
        message.success('Municipalité sauvegardée');
        setIsEditing(false);
        invalidate(queryKeys.municipalityDetail(id!));
        invalidate(queryKeys.dashboardMunicipalities);
      },
      onError: () => message.error('Erreur lors de la sauvegarde'),
    },
  );

  const deleteMutation = useWisecureMutation(
    (c) => c.municipalities.municipalityControllerRemove({ id: id! }),
    {
      onSuccess: () => {
        message.success('Municipalité supprimée');
        navigate('/municipalities');
      },
      onError: () => message.error('Erreur lors de la suppression'),
    },
  );

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload: Record<string, unknown> = {};
      if (values.name !== municipality?.name) payload.name = values.name;
      if (values.description !== (municipality?.description ?? ''))
        payload.description = values.description || undefined;

      const currentGeoIds = municipality?.geoRegions?.map((gr: GeoRegionResponse) => gr.id).sort() ?? [];
      const newGeoIds = [...(values.geoRegionIds ?? [])].sort();
      if (JSON.stringify(currentGeoIds) !== JSON.stringify(newGeoIds)) {
        payload.geoRegionIds = values.geoRegionIds;
      }

      if (Object.keys(payload).length === 0) {
        message.info('Aucune modification détectée');
        return;
      }
      updateMutation.mutate(payload);
    });
  };

  const geoRegionOptions = useMemo(() => {
    const options = selectedGeoItems.map((gr) => ({
      value: gr.id,
      label: `${gr.name} (${gr.code})`,
    }));

    if (geoRegionsData?.data) {
      for (const gr of geoRegionsData.data) {
        if (!options.some((o) => o.value === gr.id)) {
          options.push({
            value: gr.id,
            label: `${gr.name} (${gr.code})`,
          });
        }
      }
    }

    return options;
  }, [geoRegionsData, selectedGeoItems]);

  const stats = useMemo(() => {
    if (!geojson?.features?.length) return null;
    const totalArea = geojson.features.reduce(
      (s, f) => s + ((f.properties as { areaKm2?: number } | null)?.areaKm2 ?? 0),
      0,
    );
    return {
      communeCount: geojson.features.length,
      totalArea,
    };
  }, [geojson]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!municipality) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description="Municipalité non trouvée">
          <Button onClick={() => navigate('/municipalities')}>Retour à la liste</Button>
        </Empty>
      </div>
    );
  }

  const communeCount = stats?.communeCount ?? municipality.geoRegions?.length ?? 0;
  const totalArea = stats?.totalArea != null ? Number(stats.totalArea) : undefined;

  return (
    <div style={{ maxWidth: 1200 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/municipalities')}
          style={{ color: neutral[500] }}
        />
        <Text style={{ color: neutral[300], fontSize: 13 }}>Municipalités</Text>
        <Text style={{ color: neutral[200] }}>/</Text>
        <Text style={{ color: neutral[700], fontWeight: 500, fontSize: 13 }}>{municipality.name}</Text>
      </div>

      <WisecureCard
        dsVariant="highlight"
        style={{ marginBottom: 24, padding: 0 }}
        styles={{ body: { padding: '28px 32px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: primary[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BankOutlined style={{ fontSize: 24, color: neutral[0] }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0, color: neutral[0], fontWeight: 700, letterSpacing: '-0.5px' }}>
                {municipality.name}
              </Title>
              {municipality.description && (
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, display: 'block', marginTop: 4 }}>
                  {String(municipality.description)}
                </Text>
              )}
            </div>
          </div>

          <Space size={8}>
            <CopyIdButton
              id={municipality.id}
              type="primary"
              ghost
              size="small"
            />
            <ConfirmDeleteButton
              onConfirm={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
              type="primary"
              ghost
              danger
              size="small"
            >
              Supprimer
            </ConfirmDeleteButton>
          </Space>
        </div>

        <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0 16px' }} />

        <Row gutter={24}>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Communes</Text>
              <Text style={{ color: neutral[0], fontSize: 22, fontWeight: 700 }}>{communeCount}</Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Superficie</Text>
              <Text style={{ color: neutral[0], fontSize: 22, fontWeight: 700 }}>
                {totalArea ? `${totalArea.toFixed(1)} km²` : '—'}
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Créée le</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {formatDate(municipality.createdAt)}
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Mise à jour</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {formatDate(municipality.updatedAt)}
              </Text>
            </div>
          </Col>
        </Row>
      </WisecureCard>

      <Row gutter={24}>
        <Col xs={24} lg={10}>
          <WisecureCard
            style={{ marginBottom: 24 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Informations</span>
                {!isEditing && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditing(true)}
                    size="small"
                    style={{ color: primary[400] }}
                  >
                    Modifier
                  </Button>
                )}
              </div>
            }
          >
            {!isEditing ? (
              <div>
                <Row gutter={[16, 20]}>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>
                        <BankOutlined style={{ marginRight: 6 }} />
                        Nom
                      </Text>
                      <Text style={{ fontWeight: 500 }}>{municipality.name}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Identifiant</Text>
                      <Text copyable code style={{ fontSize: 12 }}>
                        {municipality.id}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Description</Text>
                      <Text style={{ fontWeight: 400 }}>
                        {municipality.description ? String(municipality.description) : 'Aucune description'}
                      </Text>
                    </div>
                  </Col>
                </Row>

                {municipality.user && (
                  <>
                    <Divider style={{ margin: '20px 0' }} />
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 10 }}>
                        <UserOutlined style={{ marginRight: 6 }} />
                        Utilisateur associé
                      </Text>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          background: neutral[50],
                          borderRadius: 10,
                          padding: '12px 14px',
                          border: `1px solid ${neutral[100]}`,
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: primary[100],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <UserOutlined style={{ fontSize: 16, color: primary[400] }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text style={{ fontWeight: 600, fontSize: 13, display: 'block' }}>
                            {[municipality.user.firstName, municipality.user.lastName]
                              .filter(Boolean)
                              .join(' ') || 'Sans nom'}
                          </Text>
                          <Space size={6}>
                            <MailOutlined style={{ color: neutral[300], fontSize: 12 }} />
                            <Text style={{ color: neutral[500], fontSize: 12 }}>
                              {municipality.user.email}
                            </Text>
                          </Space>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  name: municipality.name,
                  description: municipality.description ?? '',
                  geoRegionIds: municipality.geoRegions?.map((gr: GeoRegionResponse) => gr.id) ?? [],
                }}
                onValuesChange={(changed) => {
                  if (changed.geoRegionIds) {
                    setSelectedGeoIds(changed.geoRegionIds);
                  }
                }}
              >
                <Form.Item name="name" label="Nom" rules={[{ required: true }]}>
                  <Input prefix={<BankOutlined style={{ color: neutral[300] }} />} />
                </Form.Item>

                <Form.Item name="description" label="Description">
                  <Input.TextArea rows={2} placeholder="Description optionnelle de la municipalité" />
                </Form.Item>

                <Form.Item
                  name="geoRegionIds"
                  label="Communes assignées"
                  rules={[{ required: true, message: 'Au moins une commune est requise' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Tapez pour rechercher des communes..."
                    showSearch
                    filterOption={false}
                    onSearch={setGeoSearch}
                    loading={geoLoading}
                    options={geoRegionOptions}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                  />
                </Form.Item>

                <Divider style={{ margin: '8px 0 16px' }} />
                <Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={updateMutation.isPending}
                  >
                    Sauvegarder
                  </Button>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setIsEditing(false);
                      form.resetFields();
                      if (municipality?.geoRegions?.length) {
                        setSelectedGeoIds(municipality.geoRegions.map((gr: GeoRegionResponse) => gr.id));
                      }
                    }}
                  >
                    Annuler
                  </Button>
                </Space>
              </Form>
            )}
          </WisecureCard>
        </Col>

        <Col xs={24} lg={14}>
          <WisecureCard
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Space>
                  <EnvironmentOutlined style={{ color: primary[400] }} />
                  <span>Couverture des communes</span>
                </Space>
                {selectedGeoIds.length > 0 && (
                  <Tag
                    color={primary[50]}
                    style={{
                      color: primary[600],
                      border: `1px solid ${primary[200]}`,
                      borderRadius: 100,
                      fontWeight: 600,
                    }}
                  >
                    {selectedGeoIds.length} commune{selectedGeoIds.length > 1 ? 's' : ''}
                  </Tag>
                )}
              </div>
            }
            style={{ marginBottom: 24 }}
          >
            {selectedGeoIds.length > 0 ? (
              <>
                <CommuneCoverageStats features={(geojson?.features ?? []) as unknown as CommuneFeature[]} />
                <Divider />
                <CommuneMap geojson={geojson} loading={geojsonLoading} />
              </>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '56px 24px',
                  background: neutral[50],
                  borderRadius: 12,
                }}
              >
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background: neutral[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <EnvironmentOutlined style={{ fontSize: 28, color: neutral[300] }} />
                </div>
                <Title level={5} style={{ color: neutral[700], margin: '0 0 6px' }}>
                  Aucune commune assignée
                </Title>
                <Text style={{ color: neutral[300], fontSize: 13 }}>
                  Cliquez sur « Modifier » pour assigner des communes à cette municipalité.
                </Text>
              </div>
            )}
          </WisecureCard>
        </Col>
      </Row>
    </div>
  );
}
